import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Navbar, Nav, Modal, Button } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './PublicLayout.css';

const PublicLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeNavItem, setActiveNavItem] = useState('home');
  const [showMapModal, setShowMapModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const currentYear = new Date().getFullYear();
  const [newsStep, setNewsStep] = useState('year'); // 'year' | 'quarter'
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const yearsToShow = Array.from({ length: Math.max(1, currentYear - 2023) }, (_, idx) => currentYear - idx);

  // Map specific Year/Quarter to URLs and thumbnails
  const newsletterLinks = {
    2025: {
      Q1: {
        url: 'https://online.pubhtml5.com/zymsp/zmuk/',
        img: 'https://online.pubhtml5.com/zymsp/zmuk/files/large/1.jpg'
      },
      Q2: {
        url: 'https://online.pubhtml5.com/zymsp/lyil/',
        img: 'https://online.pubhtml5.com/zymsp/lyil/files/large/1.jpg'
      }
    },
    2024: {
      Q1: {
        url: 'https://online.pubhtml5.com/zymsp/ornm/',
        img: 'https://online.pubhtml5.com/zymsp/ornm/files/large/1.jpg'
      },
      Q2: {
        url: 'https://online.pubhtml5.com/zymsp/pizl/',
        img: 'https://online.pubhtml5.com/zymsp/pizl/files/large/1.jpg'
      },
      Q3: {
        url: 'https://online.pubhtml5.com/zymsp/tqsp/',
        img: 'https://online.pubhtml5.com/zymsp/tqsp/files/large/1.jpg'
      },
      Q4: {
        url: 'https://online.pubhtml5.com/zymsp/kqxv/',
        img: 'https://online.pubhtml5.com/zymsp/kqxv/files/large/1.jpg'
      }
    }
  };

  const getNewsletterUrl = (year, quarter) => {
    const explicit = newsletterLinks?.[year]?.[quarter];
    if (explicit && typeof explicit === 'object' && explicit.url) return explicit.url;
    const query = encodeURIComponent(`${year} ESO Newsletter for Quarter ${quarter.replace('Q','')}`);
    return `https://www.google.com/search?q=${query}`;
  };
  const heroRef = useRef(null);
  const helpRef = useRef(null);
  const footerRef = useRef(null);
  const heroImages = [
    '/carousel-bg1.jpg',
    '/carousel-bg2.jpg',
    '/carousel-bg3.jpg',
    '/carousel-bg4.jpg'
  ];
  const [heroIndex, setHeroIndex] = useState(0);


  // Initialize AOS (Animate On Scroll)
  useEffect(() => {
    const initAOS = () => {
      if (typeof window !== 'undefined' && window.AOS) {
        try {
          window.AOS.init({ duration: 700, once: true, easing: 'ease-out-cubic' });
        } catch (error) {
          console.warn('AOS initialization failed:', error);
        }
      }
    };
    
    // Add timeout to prevent hanging
    const timeoutId = setTimeout(initAOS, 100);
    return () => clearTimeout(timeoutId);
  }, []);




  // Cycle hero background images
  useEffect(() => {
    if (heroImages.length <= 1) return;
    
    try {
      const intervalId = setInterval(() => {
        setHeroIndex((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(intervalId);
    } catch (error) {
      console.warn('Hero image cycling failed:', error);
    }
  }, [heroImages.length]);

  // Smooth scroll helpers
  const scrollToHero = () => {
    setActiveNavItem('home');
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToHelp = () => {
    setActiveNavItem('about');
    if (helpRef.current) {
      helpRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleContactClick = () => {
    setActiveNavItem('contact');
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="public-layout">
      {/* Public Navigation */}
      <Navbar bg="light" variant="light" expand="lg" className="public-navbar shadow-sm sticky-top">
        <Container>
          <Navbar.Brand as={Link} to="/portal" className="d-flex align-items-center logo-section">
            <img 
              src={process.env.PUBLIC_URL + "/extension-services-logo.png"} 
              height="40" 
              className="me-3 logo-image"
              alt="Extension Services Logo"
              onError={(e) => {
                e.target.src = process.env.PUBLIC_URL + "/placeholder-logo.png";
              }}
            />
            <div className="brand-text">
              <div className="brand-title">Extension Services Office</div>
              <div className="brand-subtitle">BatState-U Lipa</div>
            </div>
          </Navbar.Brand>
          
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link 
                onClick={(e) => { e.preventDefault(); scrollToHero(); }} 
                className={activeNavItem === 'home' ? 'active' : ''}
              >
                <i className="bi bi-house me-1"></i>
                Home
              </Nav.Link>
              <Nav.Link 
                onClick={(e) => { e.preventDefault(); scrollToHelp(); }}
                className={activeNavItem === 'about' ? 'active' : ''}
              >
                <i className="bi bi-person me-1"></i>
                About Us
              </Nav.Link>
              <Nav.Link 
                onClick={(e) => { e.preventDefault(); handleContactClick(); }}
                className={activeNavItem === 'contact' ? 'active' : ''}
              >
                <i className="bi bi-telephone me-1"></i>
                Contact Us
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section 
        className="hero-section"
        ref={heroRef}
      >
        {/* Background carousel */}
        <div className="hero-bg">
          {heroImages.map((src, idx) => (
            <div
              key={src}
              className={`hero-bg-slide${idx === heroIndex ? ' active' : ''}`}
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}${src})` }}
            />
          ))}
        </div>
        <div className="hero-content">
          <div className="hero-text" data-aos="fade-up">
            <h1 className="hero-title">Welcome to the Batangas State University - TNEU</h1>
            <h2 className="hero-service">Extension Services Office Lipa Campus</h2>
            <h3 className="hero-location"></h3>
            <p className="hero-description">
              Inspiring innovations, transforming lives, and building the nation through comprehensive extension programs, educational services, and community outreach initiatives.
            </p>
            <button className="hero-button" onClick={() => setShowNewsModal(true)}>
              <i className="bi bi-book me-2"></i>
              The ESO Newsletters
            </button>
          </div>
          <div className="hero-indicators">
            {heroImages.map((_, idx) => (
              <div key={idx} className={`indicator${idx === heroIndex ? ' active' : ''}`}></div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="public-main">
        <section className="content-section" ref={helpRef}>
          <div className="container">
            <div className="services-header">
              <h2 className="content-title">How We Can Help</h2>
              <p className="services-subtitle">
                Discover the comprehensive services we offer to strengthen and support your community
              </p>
            </div>
            
            <div className="services-grid">
              <div className="service-card" data-aos="fade-right">
                <div className="service-icon">
                  <i className="bi bi-people"></i>
                </div>
                <h3 className="service-title">Community Programs</h3>
                <p className="service-description">
                  Engaging programs that bring communities together and foster collaboration.
                </p>
              </div>
              
              <div className="service-card" data-aos="fade-left">
                <div className="service-icon">
                  <i className="bi bi-book"></i>
                </div>
                <h3 className="service-title">Educational Services</h3>
                <p className="service-description">
                  Comprehensive learning opportunities for all ages and skill levels.
                </p>
              </div>
              
              <div className="service-card" data-aos="fade-right">
                <div className="service-icon">
                  <i className="bi bi-heart"></i>
                </div>
                <h3 className="service-title">Outreach Initiatives</h3>
                <p className="service-description">
                  Connecting with underserved communities to provide essential support.
                </p>
              </div>
              
              <div className="service-card" data-aos="fade-left">
                <div className="service-icon">
                  <i className="bi bi-lightbulb"></i>
                </div>
                <h3 className="service-title">Innovation Support</h3>
                <p className="service-description">
                  Helping communities develop innovative solutions to local challenges.
                </p>
              </div>
              
              <div className="service-card" data-aos="fade-right">
                <div className="service-icon">
                  <i className="bi bi-telephone"></i>
                </div>
                <h3 className="service-title">Consultation Services</h3>
                <p className="service-description">
                  Expert guidance and consultation for community development projects.
                </p>
              </div>
              
              <div className="service-card" data-aos="fade-left">
                <div className="service-icon">
                  <i className="bi bi-calendar-event"></i>
                </div>
                <h3 className="service-title">Event Planning</h3>
                <p className="service-description">
                  Professional event planning and coordination for community gatherings.
                </p>
              </div>
            </div>
          </div>
        </section>

        {children}
      </main>

      {/* Footer */}
      <footer ref={footerRef} className="public-footer" style={{ backgroundColor: '#212529', color: '#ffffff' }}>
        <Container className="py-5">
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                <div className="me-3 d-flex flex-column align-items-center">
                  <img 
                    src={process.env.PUBLIC_URL + "/batstate-u-logo.png"} 
                    alt="BatState-U Logo" 
                    height="60" 
                    style={{ borderRadius: '50%', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = process.env.PUBLIC_URL + "/placeholder-logo.png";
                    }}
                  />
                  <img 
                    src={process.env.PUBLIC_URL + "/extension-services-logo.png"}
                    alt="Extension Services Logo"
                    height="60"
                    className="mt-2"
                    style={{ objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = process.env.PUBLIC_URL + "/placeholder-logo.png";
                    }}
                  />
                </div>
                <div>
                  <h4 className="mb-1" style={{ color: '#ffffff', fontWeight: 'bold' }}>
                    Extension Services Office
                  </h4>
                  <h6 className="mb-2" style={{ color: '#ffffff' }}>
                    Batangas State University The National Engineering Univerity Lipa Campus
                  </h6>
                  <hr className="mt-3 mb-3" style={{ borderTop: '3px solid #ffffff', opacity: 1 }} />
                  <p className="mb-0" style={{ color: '#ffffff', fontSize: '1rem', marginLeft: 0, paddingLeft: 0 }}>
                    Connecting communities through extension programs and outreach services.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-start">
              <h5 className="mb-3" style={{ color: '#007bff' }}>
                Contact Info
              </h5>
              <ul className="list-unstyled ps-0">
                <li className="d-flex align-items-center mb-2" style={{ color: '#ffffff' }}>
                  <i className="bi bi-envelope me-2 flex-shrink-0"></i>
                  extension.lipa@g.batstate-u.edu.ph
                </li>
                <li className="d-flex align-items-center mb-2" style={{ color: '#ffffff' }}>
                  <i className="bi bi-telephone me-2 flex-shrink-0"></i>
                  (+63 43) 980-0385; to 94 local 3119
                </li>
                <li className="d-flex align-items-center mb-2" style={{ color: '#ffffff' }}>
                  <i className="bi bi-facebook me-2 flex-shrink-0"></i>
                  <a 
                    href="https://www.facebook.com/BSULipa.Extension" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-decoration-none"
                    style={{ color: '#ffffff' }}
                  >
                    Offical Facebook Page
                  </a>
                </li>
                <li className="d-flex align-items-center mb-2" style={{ color: '#ffffff' }}>
                  <i className="bi bi-geo-alt me-2 flex-shrink-0"></i>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); setShowMapModal(true); }}
                    className="text-decoration-none"
                    style={{ color: '#ffffff' }}
                  >
                    A. Tanco Drive, Maraouy, Lipa City, Philippines
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
          {/* ESO Newsletters Modal */}
          <Modal show={showNewsModal} onHide={() => { setShowNewsModal(false); setNewsStep('year'); }} centered>
            <Modal.Header closeButton>
              <Modal.Title className="newsletter-modal-title">BAYANIHAN</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {Object.keys(newsletterLinks)
                .map(y => parseInt(y, 10))
                .sort((a, b) => b - a)
                .map((year) => {
                  const quarters = ['Q1','Q2','Q3','Q4'].filter(q => !!newsletterLinks[year]?.[q]);
                  if (quarters.length === 0) return null;
                  return (
                    <div key={year} className="mb-3">
                      <div className="mb-2 fw-semibold">{year}</div>
                      <div className="d-flex flex-wrap gap-3">
                        {quarters.map((q) => {
                          const { url: href, img: rawImg } = newsletterLinks[year][q];
                          const imgSrc = rawImg || (process.env.PUBLIC_URL + '/placeholder.jpg');
                          return (
                            <a
                              key={q}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="newsletter-card"
                            >
                              <img
                                src={imgSrc}
                                alt={`${q} ${year}`}
                                className="newsletter-card-img"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.src = process.env.PUBLIC_URL + "/placeholder.jpg";
                                }}
                              />
                              <span className="newsletter-card-label">{q}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => { setShowNewsModal(false); setNewsStep('year'); }}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
          {/* Map Modal */}
          <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>Extension Services Office BatState-U Lipa</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ width: '100%', height: '360px' }}>
                <iframe
                  title="BatState-U Lipa Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={
                    'https://www.google.com/maps?q=' +
                    encodeURIComponent('Batangas State University Lipa Campus') +
                    '&output=embed&z=16'
                  }
                />
              </div>
              <div className="mt-3">
                <div className="fw-semibold">Address</div>
                <div>A. Tanco Drive, Maraouy 4217 Lipa City, Philippines</div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                as={Link}
                to={
                  'https://www.google.com/maps/dir/?api=1&destination=' +
                  encodeURIComponent('Batangas State University Lipa Campus')
                }
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
              >
                Get directions
              </Button>
            </Modal.Footer>
          </Modal>
          <hr style={{ borderColor: '#6c757d' }} />
          <Row>
            <Col>
              <p className="text-center mb-0" style={{ color: '#ffffff', fontSize: '0.9rem' }}>
                © 2025 Extension Services Office BatState-U Lipa. All rights reserved. | 
                <Link to="/privacy" className="text-decoration-none ms-2" style={{ color: '#ffffff' }}>
                  Privacy Policy
                </Link> | 
                <Link to="/terms" className="text-decoration-none ms-2" style={{ color: '#ffffff' }}>
                  Terms of Service
                </Link>
              </p>
            </Col>
          </Row>
        </Container>
      </footer>



    </div>
  );
};

export default PublicLayout;
